<template>
  <section class="questions-page">
    <PageTitle
      :plain="true"
      :close-button="true"
      @close="onClose"
    >{{ $domUtils.decode(dialog.title) }}</PageTitle>

    <SwipeTab
      :items="tabItems"
      v-model="activeKey"
      @change="onTabChange"
      :relaxed="true"
      :show-active="true"
    />

    <div class="questions-container">
      <div class="swiper-container" ref="container">
        <div class="swiper-wrapper">
          <div
            class="swiper-slide"
            v-for="(question, index) in questions"
            :key="question.question_id"
            :data-url="`q-${index + 1}`"
          >
            <Question :order="index + 1" :item="question" @saved="onSaved" @error="onError" />
          </div>
          <div class="swiper-slide" v-if="questions.length > 0" data-url="last">
            <QuestionLast @close="onClose" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Swiper from 'swiper'
import api from '@/api/question'
import Question from '@/components/Question.vue'
import QuestionLast from '@/components/QuestionLast.vue'
import SwipeTab from '@/components/SwipeTab.vue'
import { renderError } from '@/utils/error'
import { RESPONSE_UNAUTHORIZED } from '@/utils/error'
import QuestionClass from '@/utils/Question'

export default {
  components: {
    Question,
    QuestionLast,
    SwipeTab
  },

  navbar: false,

  metaInfo() {
    return {
      title: this.dialog.title,
      bodyAttrs: { class: 'is-primary is-footerless question-show' }
    }
  },

  data() {
    return {
      dialog: {
        title: ''
      },
      questions: [],
      swiper: null,
      fromPath: null,
      tabItems: [],
      activeKey: 0,
      hasError: false
    }
  },

  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    dialogId() {
      return this.$route.params.dialogId * 1
    },
    activeSlide() {
      return this.swiper.slides[this.swiper.activeIndex]
    },
    pageHash() {
      return this.activeSlide.dataset.url
    },
    isAnswersUpdated() {
      return this.questions.some(q => q.isAnswerUpdated())
    },
    isPartner() {
      return this.$store.getters['auth/isPartner']
    }
  },

  methods: {
    onTabChange(tabIndex) {
      this.swiper.slideTo(tabIndex)
    },
    onSaved() {
      this.updateTabItems()
      // 間髪入れず次の質問に移ると保存が完了したのかよく分からないので、一瞬待つ
      setTimeout(() => {
        window.scrollTo(0, 0)
        this.swiper.slideNext(500)
      }, 400)
    },
    onError(error) {
      this.hasError = true
      renderError(error)
    },
    createSwiper() {
      return new Swiper(this.$refs.container, {
        spaceBetween: 10,
        centeredSlides: true,
        slidesPerView: 'auto',
        grabCursor: true,
        breakpoints: {
          840: {
            spaceBetween: 20,
            // ボタンクリックなどしようとしてスライドする誤操作を防ぐ
            shortSwipes: false,
          }
        }
      })
    },
    initSwiper() {
      this.swiper = this.createSwiper()

      this.onInitSwiper()
      this.swiper.on('slideChangeTransitionEnd', this.updatePageHash)
      this.swiper.on('slideNextTransitionStart', this.onNextStart)
      this.swiper.on('slideNextTransitionEnd', this.onNextEnd)
    },
    /**
     * URLハッシュから対象の質問まで自動スクロール
     */
    slideToUrlHash() {
      const hash = document.location.hash.replace('#', '')
      if (!hash) {
        return false
      }

      const index = this.hashToIndex(hash)
      if (index === false) {
        return false
      }

      // updatePageHashを発動させたくないので、第三引数にfasleを指定する
      // -> updatePageHashを呼ぶslideChangeTransitionEndが発火しない
      // この関数が呼ばれるのは主にブラウザバック時なので、履歴を更新しないため
      this.swiper.slideTo(index, 200, false)

      // タブ移動
      this.activeKey = index
    },
    hashToIndex(hash) {
      const slide = document.querySelector(`[data-url="${hash}"]`)
      if (!slide) {
        return false
      }

      const siblings = slide.parentNode.children
      return Array.prototype.slice.call(siblings).indexOf(slide)
    },
    updatePageHash() {
      history.pushState(null, '', `${this.$route.path}#${this.pageHash}`)

      // タブ更新
      const index = this.hashToIndex(this.pageHash)
      if (index !== false) {
        this.activeKey = index
      }
    },
    onInitSwiper() {
      if (document.location.hash) {
        // URLハッシュがあれば対象の質問までスライド
        this.slideToUrlHash()
      } else {
        // 初期表示なので履歴の追加（push）ではなく置き換え（replace）
        history.replaceState(null, '', `${this.$route.path}#${this.pageHash}`)
      }

      // ブラウザバック時にURLハッシュを書き換える
      window.addEventListener('popstate', this.slideToUrlHash)
    },
    getSlidedId() {
      const index = this.swiper.previousIndex
      return this.questions[index].question_id
    },
    onNextStart() {
      this.slidings = Object.assign({}, this.slidings, {
        [this.getSlidedId()]: true
      })
    },
    onNextEnd() {
      this.slidings = Object.assign({}, this.slidings, {
        [this.getSlidedId()]: false
      })
    },
    onClose() {
      if (this.fromPath) {
        // 遷移元に戻る
        this.$router.push(this.fromPath)
      } else {
        // 遷移元なしの場合は質問一覧画面へ
        this.$wcRouter.push({ name: 'question.index' })
      }
    },
    /**
     * 質問グループ取得
     */
    async fetchDialog() {
      try {
        const dialogs = await api.dialogs(this.businessId)
        for (const dialog of dialogs) {
          if (dialog.dialog_id === this.dialogId) {
            this.dialog.title = dialog.dialog_title
            return false
          }
        }
      } catch(err) {
        renderError(err)
      }
    },
    /**
     * 質問の一覧を取得
     */
    async fetchQuestions() {
      try {
        const response = await api.questions(this.businessId, this.dialogId)

        this.questions = response.map(res => new QuestionClass(res))

        this.updateTabItems()

        this.$nextTick(() => this.initSwiper())
      } catch(err) {
        renderError(err)
      }
    },
    updateTabItems() {
      this.tabItems = [
        ...this.questions.map((q, index) => ({
          key: index,
          label: `Q${index + 1}`,
          muted: q.answered
        })),
        { key: this.questions.length, label: '完了', muted: false }
      ]
    }
  },

  created() {
    if (this.isPartner) {
      renderError({ response : { status : RESPONSE_UNAUTHORIZED}})
      return;
    }

    this.$withLoading(() => Promise.all([
      this.fetchDialog(),
      this.fetchQuestions()
    ]))
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.slideToUrlHash)
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 遷移元なしのパターン
      // 画面リロードや直接遷移など
      if (from.fullPath === '/') {
        return false
      }

      vm.fromPath = from.fullPath
    })
  },

  beforeRouteLeave(to, from, next) {
    if (this.isAnswersUpdated && !this.hasError) {
      // 未保存の更新あり
      const result = window.confirm(`まだ保存されていない回答があります。この画面から離れますか？`)

      return next(result)
    }

    next()
  }
}
</script>
